import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const revealVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] }
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.1 }
  }
};

interface AboutProps {
  showStrategicPillars?: boolean;
}

const About: React.FC<AboutProps> = ({ showStrategicPillars = true }) => {
  const [activeTab, setActiveTab] = useState<'about' | 'education' | 'approach'>('about');

  const tabs = ['about', 'education', 'approach'] as const;

  return (
    <section id="about" className="py-24 relative overflow-hidden" style={{ background: 'var(--bg-secondary)' }}>
      {/* Subtle grid bg */}
      <div className="absolute inset-0 cyber-grid-bg opacity-50" />

      <div className="max-w-7xl mx-auto px-6 relative z-10">

        {/* Section Header */}
        <motion.div
          variants={revealVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="mb-16"
        >
          <p className="section-label-cyber mb-4">About Me</p>
          <h2 className="text-4xl sm:text-5xl md:text-7xl font-black tracking-tighter leading-none">
            <span style={{ color: 'var(--text-primary)' }}>Know </span>
            <span className="gradient-text-cyber">The Operator</span>
          </h2>
        </motion.div>

        {/* Main Grid */}
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-16 items-start">

          {/* Left: Bio */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-8"
          >
            {/* Greeting */}
            <motion.div variants={revealVariants} className="cyber-card p-6 space-y-4">
              <div className="flex items-center gap-3 mb-2">
                <span className="mono text-xs" style={{ color: 'var(--neon-green)' }}>// profile.json</span>
              </div>
              <div className="mono text-sm space-y-1" style={{ color: 'var(--text-secondary)' }}>
                <p><span style={{ color: 'var(--neon-cyan)' }}>"name"</span>: <span style={{ color: '#f8c555' }}>"Darshan"</span>,</p>
                <p><span style={{ color: 'var(--neon-cyan)' }}>"role"</span>: <span style={{ color: '#f8c555' }}>"Cybersecurity Enthusiast"</span>,</p>
                <p><span style={{ color: 'var(--neon-cyan)' }}>"focus"</span>: <span style={{ color: '#f8c555' }}>"Offensive &amp; Defensive Security"</span>,</p>
                <p><span style={{ color: 'var(--neon-cyan)' }}>"status"</span>: <span style={{ color: 'var(--neon-green)' }}>"actively_learning"</span></p>
              </div>
            </motion.div>

            {/* Stats */}
            <motion.div variants={revealVariants} className="grid grid-cols-2 sm:grid-cols-3 gap-3 md:gap-4">
              {[
                { value: '8+', label: 'Projects Built', color: 'var(--neon-green)' },
                { value: '50+', label: 'CTF Challenges', color: 'var(--neon-cyan)' },
                { value: '3+', label: 'Certs Learning', color: '#7c3aed' },
              ].map((stat, i) => (
                <div
                  key={stat.label}
                  className={`cyber-card p-4 text-center ${i === 2 ? 'col-span-2 sm:col-span-1' : ''}`}
                >
                  <div
                    className="text-3xl font-black mono mb-1"
                    style={{ color: stat.color, textShadow: `0 0 15px ${stat.color}` }}
                  >
                    {stat.value}
                  </div>
                  <div className="text-xs font-medium" style={{ color: 'var(--text-muted)' }}>
                    {stat.label}
                  </div>
                </div>
              ))}
            </motion.div>

            {/* Social Links */}
            <motion.div variants={revealVariants} className="flex gap-4">
              {[
                {
                  name: 'GitHub',
                  url: 'https://github.com/darshanXtobi',
                  icon: (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" />
                    </svg>
                  )
                },
                {
                  name: 'LinkedIn',
                  url: 'https://linkedin.com/in/darshan-chipure',
                  icon: (
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                    </svg>
                  )
                },
              ].map(link => (
                <a
                  key={link.name}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-12 h-12 rounded-xl flex items-center justify-center transition-all duration-300 hover:scale-110"
                  style={{
                    background: 'var(--bg-elevated)',
                    border: '1px solid var(--border-normal)',
                    color: 'var(--text-muted)',
                  }}
                  onMouseEnter={e => {
                    (e.currentTarget as HTMLElement).style.borderColor = 'var(--neon-green)';
                    (e.currentTarget as HTMLElement).style.color = 'var(--neon-green)';
                    (e.currentTarget as HTMLElement).style.boxShadow = 'var(--glow-green)';
                  }}
                  onMouseLeave={e => {
                    (e.currentTarget as HTMLElement).style.borderColor = 'var(--border-normal)';
                    (e.currentTarget as HTMLElement).style.color = 'var(--text-muted)';
                    (e.currentTarget as HTMLElement).style.boxShadow = 'none';
                  }}
                  aria-label={link.name}
                >
                  {link.icon}
                </a>
              ))}
            </motion.div>
          </motion.div>

          {/* Right: Tabbed Content */}
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="space-y-6"
          >
            {/* Tabs */}
            <motion.div variants={revealVariants} className="flex flex-wrap sm:flex-nowrap gap-1 p-1 rounded-lg" style={{ background: 'var(--bg-elevated)' }}>
              {tabs.map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className="flex-1 py-2 px-4 rounded-md mono text-xs font-bold uppercase tracking-widest transition-all duration-300"
                  style={{
                    background: activeTab === tab ? 'rgba(0,255,136,0.15)' : 'transparent',
                    color: activeTab === tab ? 'var(--neon-green)' : 'var(--text-muted)',
                    border: activeTab === tab ? '1px solid rgba(0,255,136,0.3)' : '1px solid transparent',
                  }}
                >
                  {tab}
                </button>
              ))}
            </motion.div>

            <AnimatePresence mode="wait">
              {activeTab === 'about' && (
                <motion.div
                  key="about"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.35 }}
                  className="cyber-card p-8 space-y-6"
                >
                  <h3 className="text-2xl font-black" style={{ color: 'var(--text-primary)' }}>
                    Building at the Intersection of Security & Code
                  </h3>
                  <p className="text-base leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                    I'm a cybersecurity enthusiast and developer who believes that understanding
                    how systems break is the first step to making them unbreakable. My journey
                    started with web development, but I was always drawn to the "how does this
                    actually work under the hood?" questions.
                  </p>
                  <p className="text-base leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                    Today, I explore network vulnerabilities, practice on CTF challenges, tinker
                    with hardware like the Raspberry Pi Pico W for WiFi reconnaissance, and build
                    web tools with security-first thinking. I don't just write code—I think about
                    how it could be attacked.
                  </p>
                  <div className="flex flex-wrap gap-2 pt-2">
                    {['Problem Solver', 'Security Mindset', 'Software & Hardware Hacker', 'Open Source Advocate'].map(tag => (
                      <span key={tag} className="tech-tag">{tag}</span>
                    ))}
                  </div>
                </motion.div>
              )}

              {activeTab === 'education' && (
                <motion.div
                  key="education"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.35 }}
                  className="space-y-4"
                >
                  {[
                    {
                      year: '2023 – 2026',
                      title: 'B.Tech',
                      school: 'Current Institution',
                      detail: 'Computer Science with focus on Networking & Security',
                      color: 'var(--neon-green)'
                    },
                    {
                      year: '2026',
                      title: 'Google Cybersecurity Certificate',
                      school: 'Coursera / Google',
                      detail: 'Network security, incident response, SIEM tools',
                      color: 'var(--neon-cyan)'
                    },
                    {
                      year: 'Ongoing',
                      title: 'TryHackMe / HackTheBox',
                      school: 'Self-Paced',
                      detail: 'CTF challenges, privilege escalation, web exploitation',
                      color: '#7c3aed'
                    },
                  ].map((edu, i) => (
                    <div key={i} className="cyber-card p-6 flex gap-6 items-start">
                      <div className="mono text-xs font-bold mt-1 whitespace-nowrap" style={{ color: edu.color }}>
                        [{edu.year}]
                      </div>
                      <div>
                        <h4 className="font-black text-base" style={{ color: 'var(--text-primary)' }}>{edu.title}</h4>
                        <p className="text-sm font-medium mt-0.5" style={{ color: 'var(--text-muted)' }}>{edu.school}</p>
                        <p className="text-sm mt-2" style={{ color: 'var(--text-secondary)' }}>{edu.detail}</p>
                      </div>
                    </div>
                  ))}
                </motion.div>
              )}

              {activeTab === 'approach' && (
                <motion.div
                  key="approach"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ duration: 0.35 }}
                  className="space-y-4"
                >
                  {[
                    {
                      id: '01',
                      title: 'Think Like an Attacker.',
                      desc: 'Before writing a single line, I map attack surfaces. Understanding vulnerabilities at the system level makes my code inherently more secure.',
                      color: 'var(--neon-green)'
                    },
                    {
                      id: '02',
                      title: 'Break to Build Better.',
                      desc: 'I regularly practice on CTF platforms and lab environments to sharpen offensive skills—then apply those lessons defensively in real projects.',
                      color: 'var(--neon-cyan)'
                    },
                    {
                      id: '03',
                      title: 'Hardware Meets Software.',
                      desc: 'From Raspberry Pi Pico W WiFi tools to network packet analysis—I build across the full stack, including the physical layer.',
                      color: '#7c3aed'
                    },
                  ].map(item => (
                    <div key={item.id} className="cyber-card p-6">
                      <div className="flex gap-4">
                        <span className="mono text-sm font-bold" style={{ color: item.color }}>{item.id} /</span>
                        <div>
                          <p className="font-black text-base mb-2" style={{ color: 'var(--text-primary)' }}>{item.title}</p>
                          <p className="text-sm leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{item.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
